import React, { useRef, useState } from "react";
import useNurses from "@hooks/useNurses";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import TeamSettingContents, {
  type TeamSettingContentsHandle,
} from "./TeamSettingContents";
import CustomModal from "@/components/modal/CustomModal";
import { MSG } from "@constants/MSG";
import type { GroupMemberInMonth } from "@/api/nurses";
import type { NurseResponse } from "@vTypes/nurse-type";

interface TeamSettingModalProps {
  onClose: () => void;
  selectedGroupId?: string | null;
  selectedYear: number;
  selectedMonth: number;
  memberStatusMap?: Map<string, GroupMemberInMonth>;
  nurseDatas?: NurseResponse[];
}

const TeamSettingModal: React.FC<TeamSettingModalProps> = ({
  onClose,
  selectedGroupId,
  selectedYear,
  selectedMonth,
  memberStatusMap,
  nurseDatas,
}) => {
  const gid = selectedGroupId ?? "";
  const contentsRef = useRef<TeamSettingContentsHandle>(null);
  const [savePending, setSavePending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const { isLoading } = useTeamsQuery(gid);
  const { isLoading: isShiftsLoading } = useShiftsQuery(gid);
  const hasInjectedNurses = Array.isArray(nurseDatas);
  const { data: fallbackNurseDatas, isLoading: nurseLoading } = useNurses(gid, {
    enabled: !hasInjectedNurses,
  });
  const effectiveNurseDatas = nurseDatas ?? fallbackNurseDatas;

  const requestClose = () => {
    if (savePending) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setIsExitConfirmOpen(true);
  };

  const closeWithoutSave = () => {
    setIsExitConfirmOpen(false);
    onClose();
  };

  const saveAndClose = () => {
    contentsRef.current?.triggerSaveDirect();
  };

  return (
    <>
      <CustomModal.Confirm
        isOpen={isExitConfirmOpen}
        onClose={closeWithoutSave}
        onConfirm={saveAndClose}
        isPending={savePending}
        pendingMsg={MSG.TEAM_SAVE}
        title="변경사항을 저장하시겠습니까?"
        confirmText="저장"
        cancelText="취소"
      />
      <CustomModal.Panel
        onClose={requestClose}
        isLoading={isLoading || isShiftsLoading || nurseLoading}
        button
        btnCancelText="닫기"
        btnSuccessText="저장"
        btnCancelOnClick={requestClose}
        btnSuccessOnClick={() => contentsRef.current?.triggerSave()}
        btnSuccessDisabled={savePending}
      >
        <TeamSettingContents
          ref={contentsRef}
          onClose={onClose}
          selectedGroupId={gid}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          memberStatusMap={memberStatusMap}
          nurseDatas={effectiveNurseDatas}
          onMutationPendingChange={setSavePending}
          onDirtyChange={setIsDirty}
        />
      </CustomModal.Panel>
    </>
  );
};

export default TeamSettingModal;
